"use client";
import { useAuth } from "@/contexts/AuthContext";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { useToggle } from "@/hooks/useToggle";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { usePathname } from "@/i18n/navigation";
import clsx from "clsx";
import { useLocale } from "next-intl";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import UserMenuContent from "./UserMenuContent";
import { Button } from "./ui/Button";
import Spinner from "./ui/Spinner";

const UserButton = memo(function UserButton() {
  const { isLogin, loading, showLoginModal, getInitials, session } = useAuth();
  const t = useTranslationDetector();
  const locale = useLocale();
  const pathname = usePathname();
  const [isOpen, toggle] = useToggle();
  const ref = useRef(null);
  const escapePressed = useKeyPressed("Escape");
  const userInitials = useMemo(
    () => (isLogin && !loading ? getInitials() : ""),
    [isLogin, loading, getInitials],
  );

  const close = useCallback(() => {
    if (isOpen) {
      toggle();
    }
  }, [isOpen, toggle]);

  const handleClickOutside = useCallback(() => {
    if (isOpen) {
      close();
    }
  }, [isOpen, close]);

  useOnClickOutside(ref, handleClickOutside);

  useEffect(() => {
    if (escapePressed && isOpen) {
      close();
    }
  }, [escapePressed, isOpen, close]);

  // Sayfa değiştiğinde dropdown'ı kapat
  useEffect(() => {
    if (isOpen) {
      close();
    }
  }, [pathname]);

  const handleAvatarClick = useCallback(() => {
    toggle();
  }, [toggle]);

  // useEffect(() => {
  //   if (loading) return;

  //   sendGTMEvent({
  //     event: DATA_LAYER_EVENT_NAMES.VIRTUAL_USER_INFO,
  //     user_type: isLogin ? "member" : "guest",
  //     ...(isLogin
  //       ? {
  //           user_id: session?.UserId,
  //           user_hashed_email: session?.UserHashedEmail,
  //           user_hashed_phone: session?.UserHashedPhone,
  //           user_mail: session?.Email,
  //           user_phone:
  //             session?.MobilePhoneNumberPrefix + session?.MobilePhoneNumber,
  //         }
  //       : {
  //           ...(session?.Email && { guest_user_hashed_mail: session?.Email }),
  //           ...(session?.Phone && {
  //             guest_user_hashed_phone: session?.Phone,
  //           }),
  //         }),
  //   });
  // }, [loading]);

  if (loading) {
    return (
      <div className="flex h-11 w-11 items-center justify-center">
        <Spinner className="text-primary h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (isLogin) {
    return (
      <div ref={ref} className="relative" id="avatar">
        {/* Avatar */}
        <div
          className="flex cursor-pointer items-center space-x-2"
          onClick={handleAvatarClick}
        >
          <div className="border-primary bg-primary flex h-11 w-11 items-center justify-center rounded-full border font-medium text-white">
            {userInitials}
          </div>
        </div>

        {/* Click Menu */}
        {isOpen && (
          <div
            className={clsx(
              "shadow-xxl absolute top-full z-50 mt-2 w-[258px] rounded-lg bg-white p-4",
              locale === "ar" ? "left-0" : "right-0",
            )}
          >
            <UserMenuContent />
          </div>
        )}
      </div>
    );
  }

  const openLoginModal = () => {
    showLoginModal();
  };

  return (
    <Button
      onClick={openLoginModal}
      color="primary"
      variant="outline"
      className="min-w-30"
    >
      {t("common.login")}
    </Button>
  );
});

export default UserButton;
